/**
 * TaskContextMenu — right-click / long-press context menu for a single task.
 *
 * Positioned absolutely at the pointer location.
 * Uses shared task-actions registry + executors for consistent behavior
 * across Action Panel, context menus, and keyboard shortcuts.
 *
 * Follows master plan §21 context menu spec.
 */

import { memo, useCallback, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { useDrag } from "@use-gesture/react"
import { cn } from "@/shared/utils/general/cn"
import { shortcutLabel } from "@/shared/utils/platform/shortcutLabel"
import { ObjectStatus, ObjectType } from "@/db/schema"
import { TASK_ACTIONS, useTaskActionExecutors } from "@/features/task-actions"
import { UserStatusPicker } from "@/shared/components/UserStatusPicker"
import { useMenuPosition } from "@/shared/hooks/useMenuPosition"
import { DynamicIcon } from "@/features/navigation/DynamicIcon"
import { useRecurrence } from "@/features/recurrence"
import { SkipReasonDialog } from "@/features/recurrence/SkipReasonDialog"
import { pauseRecurrence, resumeRecurrence, addExcludeDate } from "@/db/operations"
import { startTimer, stopTimer, addSection, moveToTrash } from "@/db/operations"
import { useLiveQueryImmediate } from "@/db/hooks"
import { db } from "@/db/schema"
import {
  deleteProjectWithChildren,
  duplicateProject,
  convertProjectType,
} from "@/features/project-header/projectOps"
import { setProjectStatus } from "@/features/project-header/projectStatusRequest"
import { ROUTES } from "@/config/routes"
import { useEditFocusStore } from "@/features/task-detail/stores/editFocusStore"
import { useBreakpoint } from "@/shared/hooks/useBreakpoint"
import { hapticLight } from "@/shared/utils/platform/haptics"
import PlusIcon from "@/shared/icons/totodoicons/stroke/plus.svg?react"
import Check from "@/shared/icons/totodoicons/stroke/checkmark.svg?react"
import Ban from "@/shared/icons/pikaicons/stroke/security/ban-left.svg?react"
import SectionIcon from "@/shared/icons/totodoicons/stroke/section-add.svg?react"
import RepeatIcon from "@/shared/icons/pikaicons/stroke/arrows-chevrons/repeat-square.svg?react"
import TimerIcon from "@/shared/icons/pikaicons/stroke/time/timer-default.svg?react"
import TimerOffIcon from "@/shared/icons/pikaicons/stroke/time/timer-off.svg?react"
import EditIcon from "@/shared/icons/totodoicons/stroke/edit.svg?react"
import DuplicateIcon from "@/shared/icons/totodoicons/stroke/duplicate.svg?react"
import DeleteIcon from "@/shared/icons/totodoicons/stroke/delete.svg?react"
import ConvertIcon from "@/shared/icons/totodoicons/stroke/convert.svg?react"
import CalendarIcon from "@/shared/icons/totodoicons/stroke/calendar.svg?react"
import TagIcon from "@/shared/icons/totodoicons/stroke/tag.svg?react"
import DeadlineIcon from "@/shared/icons/totodoicons/stroke/flag.svg?react"
import ShareIcon from "@/shared/icons/totodoicons/stroke/share.svg?react"
import IconPickerIcon from "@/shared/icons/pikaicons/stroke/general/color-palette.svg?react"
import type { DbObject } from "@/db/schema"
import { useViewContext } from "@/app/stores/viewContext"
import type { ViewId } from "@/features/views/viewRegistry"

const HEADLINE_VIEWS = new Set<ViewId>(["project", "list"])

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TaskContextMenuProps {
  task: DbObject
  position: { x: number; y: number }
  onClose: () => void
  /** Open schedule picker for this task */
  onSchedule?: () => void
  /** Open move picker for this task */
  onMove?: () => void
  /** Open tag picker for this task */
  onTags?: () => void
  /** Add a new task after this one */
  onAddTask?: () => void
  /** Add a new section after this one */
  onAddSection?: () => void
  /** Open recurrence sheet for this task */
  onRepeat?: () => void
}

// ---------------------------------------------------------------------------
// Menu item helpers
// ---------------------------------------------------------------------------

interface MenuItemDef {
  label: string
  shortcut?: string
  destructive?: boolean
  disabled?: boolean
  icon?: React.ReactNode
  onClick: () => void
}

function MenuItem({
  label,
  shortcut,
  destructive,
  disabled,
  icon,
  onClick,
}: MenuItemDef) {
  const { isMobile } = useBreakpoint()
  const [swipeX, setSwipeX] = useState(0)
  const [fired, setFired] = useState(false)
  const hasFiredRef = useRef(false)

  const bind = useDrag(
    ({ movement: [mx], active, cancel }) => {
      if (!isMobile || disabled) return
      // Only allow L→R swipe
      const x = Math.max(0, mx)
      if (active) {
        setSwipeX(x)
        // Fire at 60px threshold
        if (x >= 60 && !hasFiredRef.current) {
          hasFiredRef.current = true
          setFired(true)
          hapticLight()
          cancel()
          // Execute after brief flash
          setTimeout(() => {
            onClick()
          }, 150)
        }
      } else {
        // Released before threshold — spring back
        setSwipeX(0)
        hasFiredRef.current = false
      }
    },
    { axis: "x", filterTaps: true, threshold: 8 },
  )

  return (
    <button
      {...(isMobile && !disabled ? bind() : {})}
      className={cn(
        "flex w-full items-center gap-2 rounded-lg px-3 py-1.5 text-sm outline-none transition-colors duration-150",
        "hover:bg-primary/10 focus:bg-primary/10",
        destructive && "text-destructive hover:bg-destructive/10",
        disabled && "pointer-events-none opacity-50",
        fired && "bg-primary/20",
      )}
      style={{
        transform: swipeX > 0 ? `translateX(${swipeX * 0.4}px)` : undefined,
        touchAction: "pan-y",
      }}
      onClick={onClick}
      disabled={disabled}
    >
      {icon && <span className="[&_svg]:size-4">{icon}</span>}
      <span className="flex-1 text-left">{label}</span>
      {shortcut && (
        <span className="ml-4 text-xs tracking-widest opacity-60">
          {shortcut}
        </span>
      )}
    </button>
  )
}

function MenuSeparator() {
  return <div className="my-1 h-px bg-foreground/5" />
}

/**
 * Compact icon bar (macOS Sonoma context-menu pattern).
 * Full-width buttons that evenly fill the bar width.
 * Icons: Complete ✓, Cancel ✕, Add task +, Add section H
 */
function CompactActionBar({
  isCompleted,
  isCanceled,
  onComplete,
  onCancel,
  onAdd,
  onAddSection,
}: {
  isCompleted: boolean
  isCanceled: boolean
  onComplete: () => void
  onCancel: () => void
  onAdd?: () => void
  onAddSection?: () => void
}) {
  const btnClass =
    "flex h-8 flex-1 items-center justify-center rounded-lg transition-colors duration-150 hover:bg-primary/10"
  return (
    <div className="flex items-stretch">
      <button
        className={cn(btnClass, isCompleted && "text-primary")}
        onClick={onComplete}
        title={isCompleted ? "Mark as Open" : "Complete"}
      >
        <Check className="size-5" />
      </button>
      <button
        className={cn(btnClass, isCanceled && "text-primary")}
        onClick={onCancel}
        title={isCanceled ? "Mark as Open" : "Cancel"}
      >
        <Ban className="size-5" />
      </button>
      {onAdd && (
        <button
          className={btnClass}
          onClick={onAdd}
          title="New task"
        >
          <PlusIcon className="size-5" />
        </button>
      )}
      {onAddSection && (
        <button
          className={btnClass}
          onClick={onAddSection}
          title="New section"
        >
          <SectionIcon className="size-5" />
        </button>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const TaskContextMenu = memo(function TaskContextMenu({
  task,
  position,
  onClose,
  onSchedule,
  onMove,
  onTags,
  onAddTask,
  onAddSection,
  onRepeat,
}: TaskContextMenuProps) {
  const { t } = useTranslation()
  const A = TASK_ACTIONS
  const setStatus = A["set-status"]
  const copyToClip = A["copy-to-clipboard"]
  const copyLink = A["copy-link"]
  const toSection = A["convert-to-section"]
  const toProject = A["convert-to-project"]
  const taskId = task.id
  const exec = useTaskActionExecutors(taskId ? [taskId] : [])
  const navigate = useNavigate()
  const viewId = useViewContext((s) => s.viewId)
  const canConvertToSection = HEADLINE_VIEWS.has(viewId)
  const [showStatusPicker, setShowStatusPicker] = useState(false)
  const [showSkipReasons, setShowSkipReasons] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const menuStyle = useMenuPosition(position, menuRef)

  const isCompleted = task.status === ObjectStatus.Completed
  const isCanceled = task.status === ObjectStatus.Canceled
  const isFinished = isCompleted || isCanceled

  // --- Type detection for container-specific menus -------------------------
  const isProjectType = task.type === ObjectType.Project
  const isListType = task.type === ObjectType.List
  const isContainer = isProjectType || isListType
  const isArea = task.type === ObjectType.Area
  const containerTypeLabel = isProjectType ? "project" : isListType ? "list" : "area"

  // --- Focus & Pin state ---------------------------------------------------
  const [isFocused] = useLiveQueryImmediate(
    () => taskId ? db.setting.get(`#focus:${taskId}`).then(Boolean) : false,
    ["ctx-focused", taskId],
    false,
  )
  const containerId = useViewContext((s) => s.containerId)
  const [isPinned] = useLiveQueryImmediate(
    () => (taskId && containerId) ? db.setting.get(`#pin:${containerId}:${taskId}`).then(Boolean) : false,
    ["ctx-pinned", taskId, containerId],
    false,
  )

  // --- Handlers that wrap executors + close menu ---------------------------

  const handleComplete = useCallback(() => {
    onClose()
    if (isCompleted) {
      exec.uncomplete()
    } else {
      exec.complete()
    }
  }, [onClose, isCompleted, exec])

  const handleCancel = useCallback(() => {
    onClose()
    if (isCanceled) {
      exec.uncomplete()
    } else {
      exec.cancel()
    }
  }, [onClose, isCanceled, exec])

  const handleSchedule = useCallback(() => {
    onClose()
    if (onSchedule) {
      onSchedule()
    } else {
      exec.schedule()
    }
  }, [onClose, onSchedule, exec])

  const handleEvening = useCallback(() => {
    onClose()
    exec.evening()
  }, [onClose, exec])

  const handleSomeday = useCallback(() => {
    onClose()
    exec.someday()
  }, [onClose, exec])

  const handleMove = useCallback(() => {
    onClose()
    onMove?.()
  }, [onClose, onMove])

  const handleTags = useCallback(() => {
    onClose()
    onTags?.()
  }, [onClose, onTags])

  const handleDeadline = useCallback(() => {
    onClose()
    exec.deadline()
  }, [onClose, exec])

  const handleDuplicate = useCallback(() => {
    onClose()
    exec.duplicate()
  }, [onClose, exec])

  const handleCopyToClipboard = useCallback(() => {
    onClose()
    exec.copyToClipboard()
  }, [onClose, exec])

  const handleCopyLink = useCallback(() => {
    onClose()
    exec.copyLink()
  }, [onClose, exec])

  const handleConvertToSection = useCallback(() => {
    onClose()
    exec.convertToSection()
  }, [onClose, exec])

  const handleConvertToProject = useCallback(() => {
    onClose()
    exec.convertToProject()
  }, [onClose, exec])

  const handleDelete = useCallback(() => {
    onClose()
    void exec.delete()
  }, [onClose, exec])

  const handleStatusPickerClose = useCallback(() => {
    setShowStatusPicker(false)
    onClose()
  }, [onClose])

  const handleAddTask = useCallback(() => {
    onClose()
    onAddTask?.()
  }, [onClose, onAddTask])

  const handleAddSection = useCallback(() => {
    onClose()
    onAddSection?.()
  }, [onClose, onAddSection])

  // --- Recurrence handlers -------------------------------------------------
  const isRecurringInstance = !!task.recurringTemplateId
  const isRecurringTemplate = !!task.isRecurringTemplate
  const recurrence = useRecurrence(isRecurringTemplate ? taskId : undefined)
  const isPaused = !!recurrence?.pausedAt

  const handleSkipInstance = useCallback(() => {
    setShowSkipReasons(true)
  }, [])

  const handleExcludeDate = useCallback(() => {
    onClose()
    if (!task.recurringTemplateId || !task.occurrenceKey) return
    void addExcludeDate(task.recurringTemplateId, task.occurrenceKey)
  }, [onClose, task.recurringTemplateId, task.occurrenceKey])

  const handlePauseResume = useCallback(() => {
    onClose()
    if (!taskId) return
    if (isPaused) {
      void resumeRecurrence(taskId)
    } else {
      void pauseRecurrence(taskId)
    }
  }, [onClose, taskId, isPaused])

  const handleFocus = useCallback(() => {
    onClose()
    exec.focus()
  }, [onClose, exec])

  const handlePin = useCallback(() => {
    onClose()
    exec.pin()
  }, [onClose, exec])

  const isTimerRunning = !!task.timerStartedAt
  const handleTimer = useCallback(() => {
    onClose()
    if (!taskId) return
    if (isTimerRunning) {
      void stopTimer(taskId)
    } else {
      void startTimer(taskId)
    }
  }, [onClose, taskId, isTimerRunning])

  // --- Container-specific handlers -----------------------------------------

  const handleContainerComplete = useCallback(() => {
    onClose()
    if (!taskId) return
    const newStatus = isCompleted ? ObjectStatus.Unfinished : ObjectStatus.Completed
    void setProjectStatus(taskId, newStatus)
  }, [onClose, taskId, isCompleted])

  const handleContainerCancel = useCallback(() => {
    onClose()
    if (!taskId) return
    const newStatus = isCanceled ? ObjectStatus.Unfinished : ObjectStatus.Canceled
    void setProjectStatus(taskId, newStatus)
  }, [onClose, taskId, isCanceled])

  const handleContainerRename = useCallback(() => {
    onClose()
    if (!taskId) return
    if (isProjectType) navigate(ROUTES.PROJECT(taskId))
    else if (isListType) navigate(ROUTES.LIST(taskId))
    else if (isArea) navigate(ROUTES.AREA(taskId))
    useEditFocusStore.getState().requestEditFocus(taskId)
  }, [onClose, taskId, isProjectType, isListType, isArea, navigate])

  const handleContainerSetIcon = useCallback(() => {
    onClose()
    if (!taskId) return
    useViewContext.getState().openIconPicker(taskId)
  }, [onClose, taskId])

  const handleContainerSchedule = useCallback(() => {
    onClose()
    if (!taskId) return
    useViewContext.getState().openSchedulePicker([taskId])
  }, [onClose, taskId])

  const handleContainerTags = useCallback(() => {
    onClose()
    if (!taskId) return
    useViewContext.getState().openTagPicker([taskId])
  }, [onClose, taskId])

  const handleContainerDeadline = useCallback(() => {
    onClose()
    if (!taskId) return
    useViewContext.getState().openDeadlinePicker([taskId])
  }, [onClose, taskId])

  const handleContainerAddSection = useCallback(() => {
    onClose()
    if (!taskId) return
    const parentPath = `root/${taskId}`
    void addSection(t("New Heading"), parentPath)
    if (isProjectType) navigate(ROUTES.PROJECT(taskId))
    else if (isListType) navigate(ROUTES.LIST(taskId))
  }, [onClose, taskId, isProjectType, isListType, navigate, t])

  const handleContainerDuplicate = useCallback(async () => {
    onClose()
    if (!taskId) return
    const newId = await duplicateProject(taskId)
    if (newId) {
      navigate(isProjectType ? ROUTES.PROJECT(newId) : ROUTES.LIST(newId))
    }
  }, [onClose, taskId, isProjectType, navigate])

  const handleContainerConvertType = useCallback(() => {
    onClose()
    if (!taskId) return
    void convertProjectType(taskId)
  }, [onClose, taskId])

  const handleContainerDelete = useCallback(() => {
    onClose()
    if (!taskId) return
    if (isArea) {
      void moveToTrash(taskId)
    } else {
      void deleteProjectWithChildren(taskId)
    }
  }, [onClose, taskId, isArea])

  const handleContainerShare = useCallback(() => {
    onClose()
    if (!taskId) return
    if (isProjectType) navigate(ROUTES.PROJECT(taskId))
    else if (isListType) navigate(ROUTES.LIST(taskId))
  }, [onClose, taskId, isProjectType, isListType, navigate])

  return createPortal(
    <>
      {/* Backdrop */}
      <div data-context-menu-backdrop className="fixed inset-0 z-[200] pointer-events-auto" onClick={(e) => { e.stopPropagation(); onClose() }} />

      {/* Menu panel */}
      <div
        ref={menuRef}
        className="fixed z-[200] pointer-events-auto flex min-w-[200px] flex-col overflow-hidden rounded-xl border border-foreground/5 bg-popover text-popover-foreground shadow-[0px_12px_32px_0px_#1111110F,0px_8px_4px_-8px_#1111110D]"
        style={menuStyle}
        role="menu"
      >
        {/* Scrollable middle region — everything except the pinned footer
            (Convert to Section / Convert to Project / Delete) on the default
            task menu. Area and Container menus, and the status-picker view,
            are short enough that they never need the footer split, but they
            still get the scroll region so they degrade gracefully too. */}
        {/* overflow-x-hidden is deliberate: setting only overflow-y makes the
            other axis compute from `visible` to `auto`, which would hand this
            region a horizontal scrollbar the moment any child is too wide. */}
        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain p-1.5">
          {/* ---------- Area menu ---------- */}
          {isArea ? (
            <>
              <MenuItem
                label={t("Rename")}
                icon={<EditIcon />}
                onClick={handleContainerRename}
              />
              <MenuSeparator />
              <MenuItem
                label={t("Delete area")}
                icon={<DeleteIcon />}
                destructive
                onClick={handleContainerDelete}
              />
            </>
          ) : /* ---------- Container (Project/List) menu ---------- */
          isContainer ? (
            <>
              <MenuItem
                label={t("Rename")}
                icon={<EditIcon />}
                onClick={handleContainerRename}
              />
              <MenuItem
                label={t("Set Icon…")}
                icon={<IconPickerIcon />}
                onClick={handleContainerSetIcon}
              />
              <MenuSeparator />
              <MenuItem
                label={isCompleted ? t("Reopen") : t("Complete {{type}}", { type: t(containerTypeLabel) })}
                icon={<Check />}
                onClick={handleContainerComplete}
              />
              <MenuItem
                label={isCanceled ? t("Reopen") : t("Cancel {{type}}", { type: t(containerTypeLabel) })}
                icon={<Ban />}
                onClick={handleContainerCancel}
              />
              <MenuSeparator />
              <MenuItem
                label={t("When…")}
                icon={<CalendarIcon />}
                onClick={handleContainerSchedule}
                disabled={isFinished}
              />
              <MenuItem
                label={t("Tags…")}
                icon={<TagIcon />}
                onClick={handleContainerTags}
              />
              <MenuItem
                label={t("Deadline…")}
                icon={<DeadlineIcon />}
                onClick={handleContainerDeadline}
                disabled={isFinished}
              />
              <MenuItem
                label={t("Add Section")}
                icon={<SectionIcon />}
                onClick={handleContainerAddSection}
              />
              <MenuSeparator />
              <MenuItem
                label={t("Duplicate")}
                icon={<DuplicateIcon />}
                onClick={() => void handleContainerDuplicate()}
              />
              <MenuItem
                label={t("Convert to {{type}}", { type: isProjectType ? t("list") : t("project") })}
                icon={<ConvertIcon />}
                onClick={handleContainerConvertType}
              />
              <MenuSeparator />
              <MenuItem
                label={t("Delete {{type}}", { type: t(containerTypeLabel) })}
                icon={<DeleteIcon />}
                destructive
                onClick={handleContainerDelete}
              />
              <MenuSeparator />
              <MenuItem
                label={t("Share…")}
                icon={<ShareIcon />}
                onClick={handleContainerShare}
              />
            </>
          ) : /* ---------- Task menu (default) ---------- */
          showStatusPicker ? (
            /* Status picker — replaces main menu content */
            <div className="flex min-h-0 min-w-[180px] flex-col overflow-hidden">
              <UserStatusPicker
                taskId={taskId ?? ""}
                currentValue={task.userStatus}
                onClose={handleStatusPickerClose}
              />
            </div>
          ) : (
            <>
              {/* Compact action bar: ✓ ✕ + H */}
              <CompactActionBar
                isCompleted={isCompleted}
                isCanceled={isCanceled}
                onComplete={handleComplete}
                onCancel={handleCancel}
                onAdd={onAddTask ? handleAddTask : undefined}
                onAddSection={onAddSection ? handleAddSection : undefined}
              />

              <MenuSeparator />

              {/* Schedule */}
              <MenuItem
                label={t(A.schedule.label)}
                shortcut={shortcutLabel(A.schedule.shortcut!)}
                icon={<A.schedule.icon />}
                onClick={handleSchedule}
                disabled={isFinished}
              />
              <MenuItem
                label={t(A.evening.label)}
                shortcut={shortcutLabel(A.evening.shortcut!)}
                icon={<A.evening.icon />}
                onClick={handleEvening}
                disabled={isFinished}
              />
              <MenuItem
                label={t(A.someday.label)}
                shortcut={shortcutLabel(A.someday.shortcut!)}
                icon={<A.someday.icon />}
                onClick={handleSomeday}
                disabled={isFinished}
              />
              <MenuItem
                label={t(A.move.label)}
                shortcut={shortcutLabel(A.move.shortcut!)}
                icon={<A.move.icon />}
                onClick={handleMove}
              />
              <MenuItem
                label={t(A.tags.label)}
                shortcut={shortcutLabel(A.tags.shortcut!)}
                icon={<A.tags.icon />}
                onClick={handleTags}
              />

              {/* Recurrence actions */}
              {!isRecurringInstance && !isRecurringTemplate && onRepeat && (
                <MenuItem
                  label={t("recurrence.title", "Repeat")}
                  icon={<RepeatIcon />}
                  onClick={() => { onClose(); onRepeat() }}
                />
              )}
              {isRecurringInstance && (
                <>
                  <MenuSeparator />
                  {showSkipReasons ? (
                    <SkipReasonDialog
                      instanceId={taskId!}
                      onClose={onClose}
                    />
                  ) : (
                    <MenuItem
                      label={t("recurrence.skipInstance", "Skip this instance")}
                      icon={<RepeatIcon />}
                      onClick={handleSkipInstance}
                    />
                  )}
                  <MenuItem
                    label={t("recurrence.excludeDate", "Always skip this date")}
                    icon={<Ban />}
                    onClick={handleExcludeDate}
                  />
                </>
              )}
              {isRecurringTemplate && (
                <>
                  <MenuSeparator />
                  <MenuItem
                    label={isPaused
                      ? t("recurrence.resume", "Resume repeating")
                      : t("recurrence.pause", "Pause repeating")
                    }
                    icon={<RepeatIcon />}
                    onClick={handlePauseResume}
                  />
                </>
              )}

              <MenuSeparator />

              {/* Deadline + Set Status */}
              <MenuItem
                label={t(A.deadline.label)}
                shortcut={shortcutLabel(A.deadline.shortcut!)}
                icon={<A.deadline.icon />}
                onClick={handleDeadline}
                disabled={isFinished}
              />
              <MenuItem
                label={t(setStatus.label)}
                shortcut={shortcutLabel(setStatus.shortcut!)}
                icon={<setStatus.icon />}
                onClick={() => setShowStatusPicker(true)}
              />

              {/* Focus & Pin */}
              <MenuItem
                label={isFocused ? t("Remove from Focus") : t(A.focus.label)}
                icon={<A.focus.icon />}
                onClick={handleFocus}
                disabled={isFinished}
              />
              {containerId && (
                <MenuItem
                  label={isPinned ? t("Unpin") : t(A.pin.label)}
                  icon={<A.pin.icon />}
                  onClick={handlePin}
                  disabled={isFinished}
                />
              )}
              <MenuItem
                label={isTimerRunning ? t("Stop Timer") : t("Start Timer")}
                icon={isTimerRunning ? <TimerOffIcon /> : <TimerIcon />}
                onClick={handleTimer}
                shortcut="T"
                disabled={isFinished}
              />

              <MenuSeparator />

              {/* Organize */}
              <MenuItem
                label={t(A.duplicate.label)}
                shortcut={shortcutLabel(A.duplicate.shortcut!)}
                icon={<A.duplicate.icon />}
                onClick={handleDuplicate}
              />
              <MenuItem
                label={t(copyToClip.label)}
                icon={<copyToClip.icon />}
                onClick={handleCopyToClipboard}
              />
              <MenuItem
                label={t(copyLink.label)}
                shortcut={copyLink.shortcut ? shortcutLabel(copyLink.shortcut) : undefined}
                icon={<copyLink.icon />}
                onClick={handleCopyLink}
              />
            </>
          )}
        </div>

        {/* Pinned footer — Convert + Delete stay reachable regardless of how
            tall the scrollable content above is. Only rendered for the
            default task menu; Area/Container menus and the status picker
            have no trailing actions that need pinning. */}
        {!isArea && !isContainer && !showStatusPicker && (
          <div className="shrink-0 border-t border-foreground/5 p-1.5">
            <MenuSeparator />

            {/* Convert */}
            <MenuItem
              label={t(toSection.label)}
              icon={<toSection.icon />}
              onClick={handleConvertToSection}
              disabled={!canConvertToSection}
            />
            <MenuItem
              label={t(toProject.label)}
              icon={
                <DynamicIcon
                  type="project"
                  percentage={33}
                  unfinished={0}
                  status={ObjectStatus.Unfinished}
                  color="currentColor"
                  size="xs"
                />
              }
              onClick={handleConvertToProject}
            />

            <MenuSeparator />

            {/* Destructive */}
            <MenuItem
              label={t(A.delete.label)}
              shortcut="⌫"
              icon={<A.delete.icon />}
              destructive
              onClick={handleDelete}
            />

            {/* iOS home indicator clearance */}
            <div className="safe-area-bottom" />
          </div>
        )}
      </div>
    </>,
    document.body,
  )
})
